import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function GitHubCopilotCompliancePage() {
  const faqs = [
    {
      question: 'How does EvidentTrail detect GitHub Copilot usage?',
      answer:
        'EvidentTrail reads explicit GitHub metadata signals. When GitHub Copilot generates code, it adds a co-author trailer to the commit — "Co-authored-by: GitHub Copilot <copilot@github.com>". EvidentTrail captures this trailer and flags the commit as AI-assisted. No source code, no prompt history, and no generated content are read or stored — only the metadata that proves AI involvement.',
    },
    {
      question: 'What compliance frameworks require human oversight of Copilot-generated code?',
      answer:
        'The EU AI Act (Article 14) requires human oversight mechanisms for high-risk AI systems. ISO 42001 (AI Management Systems) requires documented human review of AI-generated outputs. SOC 2 auditors increasingly ask how organizations ensure code quality and access control when AI tools generate code. Security frameworks like NIST AI RMF recommend human review checkpoints in AI-assisted development workflows.',
    },
    {
      question: 'Does EvidentTrail read the code that Copilot generated?',
      answer:
        'No. EvidentTrail reads only GitHub metadata — commit co-author trailers, pull request reviewer records, approval timestamps, and CI check results. It does not access, read, or store source code, prompts, or Copilot suggestion history.',
    },
    {
      question: 'What if our team does not use co-author trailers consistently?',
      answer:
        'EvidentTrail also detects AI signals from GitHub Copilot labels on pull requests, bot author identifiers, and other metadata signals that indicate AI involvement. For organizations that want consistent signal capture, we recommend enforcing co-author trailer policies via branch protection rules or pre-commit hooks — the evidence is only as complete as the signals your team generates.',
    },
    {
      question: 'Can I export Copilot compliance evidence for an auditor?',
      answer:
        'Yes. Evidence packs include all AI signal detections alongside their linked human approval records, packaged in PDF, CSV, and JSON formats with SHA-256 checksums. Auditors receive a tamper-evident export showing: which changes involved AI assistance, who reviewed and approved them, and what CI checks passed before merge.',
    },
  ];

  const longformSections = [
    {
      title: 'The audit question Copilot creates',
      paragraphs: [
        'GitHub Copilot is now used by millions of developers to generate code, suggest completions, and accelerate pull request creation. For most teams, adoption has been fast and largely informal — developers use Copilot in their editors without any systematic tracking of which code was AI-generated.',
        'This creates a gap in compliance documentation. When a SOC 2 auditor or ISO 27001 assessor samples pull requests and asks "how do you know this code was reviewed by a qualified human?", the honest answer for many teams is that they cannot distinguish AI-generated code from human-written code in their evidence records. The reviewer may have approved without knowing how much of the PR was Copilot output.',
        'Regulations are moving to close this gap. The EU AI Act requires human oversight for high-risk AI systems. ISO 42001 requires documented review processes for AI-generated outputs. SOC 2 auditors are beginning to ask specifically about AI coding assistants in security reviews. The teams that have evidence now will be ahead of requirements that are being formalized through 2026 and 2027.',
      ],
      bullets: [
        'EU AI Act Article 14: human oversight required for high-risk AI systems — August 2026 enforcement',
        'ISO 42001: AI management systems standard requires documented AI output review',
        'SOC 2 auditors increasingly ask about AI coding assistant controls',
        'NIST AI RMF recommends human review checkpoints in AI-assisted development',
      ],
    },
    {
      title: 'How EvidentTrail detects Copilot signals',
      paragraphs: [
        'When a developer accepts a Copilot suggestion and commits the code, GitHub records a co-author trailer in the commit: "Co-authored-by: GitHub Copilot <copilot@github.com>". This is the primary, most reliable signal of Copilot involvement, and it is entirely metadata — no code content is transmitted.',
        'EvidentTrail reads this trailer from the commit metadata and associates it with the pull request record. The evidence shows: this PR contained commits with Copilot co-authorship, the PR was reviewed by a named human reviewer, the reviewer approved after the AI signal was present, and required CI checks passed. This is the evidence chain auditors need to verify human oversight.',
        'Additional signals EvidentTrail captures include GitHub Copilot labels applied to pull requests, bot author identifiers in commit history, and any custom labels your team uses to flag AI-assisted work. The detection is deterministic — based only on explicit metadata — not probabilistic code analysis.',
      ],
      bullets: [
        'Co-author trailers: "Co-authored-by: GitHub Copilot <copilot@github.com>"',
        'PR labels indicating AI assistance',
        'Bot author identifiers in commit history',
        'Custom team-defined AI signal labels',
      ],
    },
    {
      title: 'What the evidence record looks like',
      paragraphs: [
        'Each pull request containing a Copilot signal generates an evidence record structured as: AI signal detected (co-author trailer, timestamp), human reviewer assigned (reviewer identity), human review completed (review timestamp), approval decision (approved/changes requested), CI checks passed (check name, result, timestamp), merge completed (merge timestamp, merge commit SHA).',
        'This record answers the audit question directly: an AI tool contributed to this change, a qualified human reviewed it before merge, the required automated checks passed, and the change was controlled throughout. The evidence is system-generated from GitHub metadata — not manually assembled — which means it is reproducible and consistent.',
        'Evidence packs export this structured data in PDF format for auditor review, CSV for analysis, and JSON for GRC platform import. Every pack includes a SHA-256 manifest so the auditor can verify the export was not modified after generation.',
      ],
      bullets: [
        'AI signal timestamp linked to the specific commit and PR',
        'Human reviewer identity and approval timestamp',
        'CI check results associated with the same PR',
        'SHA-256 checksum for tamper-evident delivery',
      ],
    },
    {
      title: 'What auditors ask about Copilot',
      paragraphs: [
        'Based on emerging audit practice, the most common questions about AI coding assistants in compliance reviews are: Does your organization know when AI tools were used to generate production code? What controls ensure a human reviewed AI-generated code before deployment? How do you demonstrate that code review was effective rather than rubber-stamped? Can you produce evidence of this oversight for a sampled set of changes?',
        'EvidentTrail directly answers all four questions with evidence rather than policy documents. The audit trail shows which PRs contained AI signals, who reviewed them, and what controls were in place — without requiring the organization to trust that humans followed a policy.',
      ],
      bullets: [
        '"Does your organization know when AI tools generated production code?" — Yes, via co-author trail capture',
        '"What controls ensure human review?" — Branch protection requiring approval before merge',
        '"Can you demonstrate effective review?" — Reviewer identity, timestamp, and CI results linked to AI signal',
        '"Can you sample evidence?" — Evidence pack scoped to audit period, exportable on demand',
      ],
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'The most common audit gaps for teams using GitHub Copilot are: no systematic capture of co-author trailers (developers use Copilot but trailers are not added consistently), reviews that approved PRs without recognizing AI involvement (the reviewer did not know Copilot was used), and no exception process for AI-assisted code that bypasses normal review.',
        'EvidentTrail surfaces the first issue — inconsistent signal capture — by showing which PRs had AI signals and which did not, allowing teams to audit their own Copilot usage patterns. The second issue is addressed by making AI signals visible in the review evidence, creating accountability for human oversight. The third is addressed by exception tracking that records any merge bypassing the standard review gate.',
      ],
      bullets: [
        'Inconsistent co-author trailers: EvidentTrail shows coverage gaps',
        'Reviews without AI awareness: signals visible in evidence record',
        'Bypassed review gates: exception records with AI signal linkage',
      ],
    },
  ];

  return (
    <MarketingLayout
      title="GitHub Copilot Compliance Evidence — Human Oversight Audit Trail | EvidentTrail"
      description="Capture GitHub Copilot co-author signals and link them to human PR approvals. Audit evidence for EU AI Act Article 14, ISO 42001, and SOC 2 human oversight requirements."
      h1="GitHub Copilot Compliance Evidence"
      heroText="Prove human oversight of Copilot-generated code. EvidentTrail detects AI co-author signals, links them to human reviewer approvals, and exports audit-ready evidence for EU AI Act and SOC 2 reviews."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'GitHub Copilot Compliance', url: 'https://evidenttrail.com/github-copilot-compliance' },
        ]}
      />
      <FaqJsonLd items={faqs} />

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">

            {/* The evidence chain */}
            <h2 className={`${tokens.heading.h2} mb-6`}>The Copilot Evidence Chain</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Each Copilot-assisted pull request generates a complete evidence chain from AI signal to human approval to production merge.
            </p>

            <div className="relative mb-12">
              {[
                {
                  step: '1',
                  icon: '🤖',
                  title: 'AI Signal Detected',
                  detail: 'Copilot co-author trailer captured from commit metadata. Timestamp recorded. No code content read.',
                },
                {
                  step: '2',
                  icon: '👀',
                  title: 'Human Review Assigned',
                  detail: 'Branch protection requires reviewer assignment. Reviewer identity and assignment timestamp recorded.',
                },
                {
                  step: '3',
                  icon: '✅',
                  title: 'Approval with Oversight',
                  detail: 'Human approval decision and timestamp linked to the AI signal. CI checks passed.',
                },
                {
                  step: '4',
                  icon: '📜',
                  title: 'Evidence Pack Generated',
                  detail: 'Structured record: AI signal → Human reviewer → Approval → CI pass → Merge. SHA-256 checksum included.',
                },
              ].map(({ step, icon, title, detail }) => (
                <div key={step} className="flex items-start gap-4 mb-6">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm">
                    {step}
                  </div>
                  <div className={`${tokens.card} flex-1`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{icon}</span>
                      <h3 className="text-white font-semibold">{title}</h3>
                    </div>
                    <p className="text-sm text-slate-400">{detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Regulatory context */}
            <h2 className={`${tokens.heading.h2} mb-6`}>Regulatory Requirements</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              {[
                {
                  framework: 'EU AI Act — Article 14',
                  requirement: 'Human oversight mechanisms for high-risk AI systems. Enforcement: August 2, 2026.',
                  link: '/eu-ai-act-github-compliance',
                },
                {
                  framework: 'ISO 42001',
                  requirement: 'AI management systems standard. Documented review processes for AI-generated outputs.',
                  link: '/ai-assisted-development-evidence',
                },
                {
                  framework: 'SOC 2 CC8.1',
                  requirement: 'Change management controls. Auditors increasingly ask about AI coding assistant oversight.',
                  link: '/soc2-change-management-evidence',
                },
                {
                  framework: 'ISO 27001 A.8.32',
                  requirement: 'Change management requires authorized, reviewed, and documented changes — including AI-assisted ones.',
                  link: '/solutions/iso-27001-a832-evidence',
                },
              ].map(({ framework, requirement, link }) => (
                <Link key={framework} to={link} className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                  <h3 className="text-primary-400 font-medium text-sm mb-1">{framework}</h3>
                  <p className="text-sm text-slate-400">{requirement}</p>
                </Link>
              ))}
            </div>

            <div className="text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Start Tracking Copilot Oversight
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related AI Evidence Resources</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/ai-assisted-development-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">AI-Assisted Dev Evidence</h4>
              <p className="text-sm text-slate-400">Broader overview of AI coding assistant audit evidence.</p>
            </Link>
            <Link to="/eu-ai-act-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">EU AI Act GitHub Guide</h4>
              <p className="text-sm text-slate-400">Article 14 human oversight requirements — August 2026 deadline.</p>
            </Link>
            <Link to="/tools/ai-risk-classifier" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">AI Risk Classifier</h4>
              <p className="text-sm text-slate-400">Free tool — assess your EU AI Act risk level in 2 minutes.</p>
            </Link>
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">PR Approvals Report</h4>
              <p className="text-sm text-slate-400">Free tool — export human approval evidence for a repository.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
